import { ChangeDetectionStrategy, Component, ElementRef, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SelectValueSyncDirective } from '../../../../../../../shared/directives/select-value-sync.directive';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { WorkflowNodeConfigBase } from './node-config-base';
import {
  DEFAULT_WORKFLOW_TIMEZONE,
  PRIORITY_OPTIONS,
  TASK_TYPE_OPTIONS,
  WAIT_UNIT_OPTIONS,
  WORKFLOW_TIMEZONE_OPTIONS
} from './config-options';
import { EntitySelectComponent, EntitySelectPage } from '../../../../../../../shared/components/entity-select/entity-select.component';
import { WorkflowVariablePickerComponent } from '../variable-picker/variable-picker.component';
import {
  UserDirectoryApiService,
  DirectoryUser,
  directoryUserLabel
} from '../../../../../../../shared/services/user-directory-api.service';
import { TaskCatalogApiService } from '../../../../../../tasks/services/task-catalog-api.service';
import { TaskApiService } from '../../../../../../tasks/services/task-api.service';
import { MeetingTypeApiService } from '../../../../../../meeting/services/meeting-type-api.service';
import { AuthService } from '../../../../../../auth-pages/services/auth.service';
import { TaskResponse } from '../../../../../../tasks/models/task.model';
import { WorkflowPropertySelectComponent } from './property-select.component';
import { buildPropertyOptions } from '../../../models/workflow-variable-catalog';

interface AttendeeRow { type: string; emailProperty: string; email: string; }

/**
 * ACTION → Tarea y actividad: CREATE_TASK, CREATE_NOTE, COMPLETE_TASK, SCHEDULE_MEETING.
 * Portado 1:1 del panel viejo.
 */
@Component({
  selector: 'app-wf-task-activity-action-config',
  standalone: true,
  imports: [CommonModule, SelectValueSyncDirective, EntitySelectComponent, WorkflowVariablePickerComponent, WorkflowPropertySelectComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @switch (subtype()) {
      @case ('CREATE_TASK') {
        <div class="space-y-4">
          <div>
            <div class="mb-1.5 flex items-center justify-between">
              <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Título</label>
              <app-wf-variable-picker [target]="titleField()?.nativeElement ?? null" [disabled]="readOnly()"
                                      (valueChange)="patchConfig({ title: $event })" />
            </div>
            <input #titleInput type="text" [class]="fieldClass" [value]="str('title')" [disabled]="readOnly()"
                   (input)="patchConfig({ title: inputValue($event) })" />
          </div>
          <div>
            <div class="mb-1.5 flex items-center justify-between">
              <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Descripción</label>
              <app-wf-variable-picker [target]="descriptionField()?.nativeElement ?? null" [disabled]="readOnly()"
                                      (valueChange)="patchConfig({ description: $event })" />
            </div>
            <textarea #descriptionInput rows="2" [class]="fieldClass" [value]="str('description')" [disabled]="readOnly()"
                      (input)="patchConfig({ description: inputValue($event) })"></textarea>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label [class]="labelClass">Tipo</label>
              <select [class]="fieldClass" [value]="str('type')" [disabled]="readOnly()"
                      (change)="patchConfig({ type: inputValue($event) })">
                <option value="">Por defecto del catálogo</option>
                @for (t of taskTypeOptions(); track t.value) { <option [value]="t.value">{{ t.label }}</option> }
                @if (customTypeValue()) { <option [value]="customTypeValue()">{{ customTypeValue() }} (no está en el catálogo)</option> }
              </select>
            </div>
            <div>
              <label [class]="labelClass">Prioridad</label>
              <select [class]="fieldClass" [value]="str('priority')" [disabled]="readOnly()"
                      (change)="patchConfig({ priority: inputValue($event) })">
                <option value="">Por defecto del catálogo</option>
                @for (p of priorityOptions(); track p.value) { <option [value]="p.value">{{ p.label }}</option> }
                @if (customPriorityValue()) { <option [value]="customPriorityValue()">{{ customPriorityValue() }} (no está en el catálogo)</option> }
              </select>
            </div>
          </div>
          <div>
            <label [class]="labelClass">Vencimiento</label>
            <select [class]="fieldClass" [value]="nestedStr('due', 'type', 'RELATIVE')" [disabled]="readOnly()"
                    (change)="setDueType(inputValue($event))">
              <option value="RELATIVE">Relativo</option>
              <option value="FIXED">Fecha fija</option>
              <option value="PROPERTY_BASED">Basado en propiedad</option>
            </select>
          </div>
          @if (nestedStr('due', 'type', 'RELATIVE') === 'FIXED') {
            <div>
              <label [class]="labelClass">Fecha y hora</label>
              <input type="datetime-local" [class]="fieldClass" [value]="nestedStr('due', 'datetime')" [disabled]="readOnly()"
                     (change)="patchNested('due', { type: 'FIXED', datetime: inputValue($event) })" />
            </div>
          } @else if (nestedStr('due', 'type', 'RELATIVE') === 'PROPERTY_BASED') {
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label [class]="labelClass">Propiedad</label>
                <app-wf-property-select
                  [value]="nestedStr('due', 'property')" [options]="propertyOptions()" [disabled]="readOnly()"
                  placeholder="Elige una propiedad…"
                  (valueChange)="patchNested('due', { type: 'PROPERTY_BASED', property: $event })"></app-wf-property-select>
              </div>
              <div>
                <label [class]="labelClass">Unidad</label>
                <select [class]="fieldClass" [value]="nestedStr('due', 'unit', 'DAYS')" [disabled]="readOnly()"
                        (change)="patchNested('due', { unit: inputValue($event) })">
                  @for (u of unitOptions; track u.value) { <option [value]="u.value">{{ u.label }}</option> }
                </select>
              </div>
            </div>
          } @else {
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label [class]="labelClass">Cantidad</label>
                <input type="number" min="1" [class]="fieldClass" [value]="nestedNum('due', 'amount', 1)" [disabled]="readOnly()"
                       (change)="patchNested('due', { type: 'RELATIVE', amount: numberValue($event) })" />
              </div>
              <div>
                <label [class]="labelClass">Unidad</label>
                <select [class]="fieldClass" [value]="nestedStr('due', 'unit', 'DAYS')" [disabled]="readOnly()"
                        (change)="patchNested('due', { unit: inputValue($event) })">
                  @for (u of unitOptions; track u.value) { <option [value]="u.value">{{ u.label }}</option> }
                </select>
              </div>
            </div>
          }
          <div>
            <label [class]="labelClass">Asignar a</label>
            <select [class]="fieldClass" [value]="nestedStr('assignee', 'type', 'OWNER')" [disabled]="readOnly()"
                    (change)="setAssigneeType(inputValue($event))">
              <option value="OWNER">Responsable del registro</option>
              <option value="USER">Usuario específico</option>
            </select>
          </div>
          @if (nestedStr('assignee', 'type', 'OWNER') === 'USER') {
            <div>
              <label [class]="labelClass">Usuario</label>
              <app-entity-select
                [value]="assigneeUserId() || null" [displayText]="assigneeName()"
                [searchFn]="userSearch" [labelFn]="userLabel" [sublabelFn]="userSublabel" [disabled]="readOnly()"
                placeholder="Buscar usuario…"
                (valueChange)="patchNested('assignee', { type: 'USER', userId: $event })" (selected)="onAssigneeSelected($event)"></app-entity-select>
            </div>
          }
          <div>
            <div class="mb-1.5 flex items-center justify-between">
              <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Notas</label>
              <app-wf-variable-picker [target]="notesField()?.nativeElement ?? null" [disabled]="readOnly()"
                                      (valueChange)="patchConfig({ notes: $event })" />
            </div>
            <textarea #notesInput rows="2" [class]="fieldClass" [value]="str('notes')" [disabled]="readOnly()"
                      (input)="patchConfig({ notes: inputValue($event) })"></textarea>
          </div>
          <ng-container [ngTemplateOutlet]="associate" />
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('CREATE_NOTE') {
        <div class="space-y-4">
          <div>
            <div class="mb-1.5 flex items-center justify-between">
              <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Texto</label>
              <app-wf-variable-picker [target]="noteTextField()?.nativeElement ?? null" [disabled]="readOnly()"
                                      (valueChange)="patchConfig({ text: $event })" />
            </div>
            <textarea #noteTextInput rows="4" [class]="fieldClass" [value]="str('text')" [disabled]="readOnly()"
                      (input)="patchConfig({ text: inputValue($event) })"></textarea>
          </div>
          <div>
            <label [class]="labelClass">Visibilidad</label>
            <select [class]="fieldClass" [value]="str('visibility', 'INTERNAL')" [disabled]="readOnly()"
                    (change)="patchConfig({ visibility: inputValue($event) })">
              <option value="INTERNAL">Interna</option>
              <option value="PUBLIC">Pública</option>
            </select>
          </div>
          <ng-container [ngTemplateOutlet]="associate" />
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('COMPLETE_TASK') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Selector de tarea</label>
            <select [class]="fieldClass" [value]="nestedStr('taskSelector', 'type', 'LATEST_PENDING_FOR_RECORD')" [disabled]="readOnly()"
                    (change)="setSelectorType(inputValue($event))">
              <option value="LATEST_PENDING_FOR_RECORD">Última pendiente del registro</option>
              <option value="EXPLICIT_TASK_ID">Por id</option>
              <option value="MATCH_BY_TITLE">Por título</option>
              <option value="MATCH_BY_TYPE">Por tipo</option>
            </select>
          </div>
          @if (nestedStr('taskSelector', 'type', '') === 'EXPLICIT_TASK_ID') {
            <div>
              <label [class]="labelClass">Tarea</label>
              <app-entity-select
                [value]="taskSelectorId() || null" [displayText]="taskName()"
                [searchFn]="taskSearch" [labelFn]="taskLabel" [sublabelFn]="taskSublabel" [disabled]="readOnly()"
                placeholder="Buscar tarea por título…"
                (valueChange)="patchNested('taskSelector', { type: 'EXPLICIT_TASK_ID', taskId: $event })"
                (selected)="onTaskSelected($event)"></app-entity-select>
            </div>
          }
          @if (nestedStr('taskSelector', 'type', '') === 'MATCH_BY_TITLE') {
            <div>
              <label [class]="labelClass">El título contiene</label>
              <input type="text" [class]="fieldClass" [value]="nestedStr('taskSelector', 'titleContains')" [disabled]="readOnly()"
                     (input)="patchNested('taskSelector', { type: 'MATCH_BY_TITLE', titleContains: inputValue($event) })" />
            </div>
          }
          @if (nestedStr('taskSelector', 'type', '') === 'MATCH_BY_TYPE') {
            <div>
              <label [class]="labelClass">Tipo de tarea</label>
              <select [class]="fieldClass" [value]="nestedStr('taskSelector', 'taskType')" [disabled]="readOnly()"
                      (change)="patchNested('taskSelector', { type: 'MATCH_BY_TYPE', taskType: inputValue($event) })">
                <option value="">Elige un tipo…</option>
                @for (t of taskTypeOptions(); track t.value) { <option [value]="t.value">{{ t.label }}</option> }
                @if (customTaskSelectorType()) { <option [value]="customTaskSelectorType()">{{ customTaskSelectorType() }}</option> }
              </select>
            </div>
          }
          <div>
            <div class="mb-1.5 flex items-center justify-between">
              <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Nota de finalización (opcional)</label>
              <app-wf-variable-picker [target]="completionNoteField()?.nativeElement ?? null" [disabled]="readOnly()"
                                      (valueChange)="patchConfig({ completionNote: $event })" />
            </div>
            <input #completionNoteInput type="text" [class]="fieldClass" [value]="str('completionNote')" [disabled]="readOnly()"
                   (input)="patchConfig({ completionNote: inputValue($event) })" />
          </div>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('SCHEDULE_MEETING') {
        <div class="space-y-4">
          <div>
            <div class="mb-1.5 flex items-center justify-between">
              <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Título</label>
              <app-wf-variable-picker [target]="titleField()?.nativeElement ?? null" [disabled]="readOnly()"
                                      (valueChange)="patchConfig({ title: $event })" />
            </div>
            <input #titleInput type="text" [class]="fieldClass" [value]="str('title')" [disabled]="readOnly()"
                   (input)="patchConfig({ title: inputValue($event) })" />
          </div>
          <div>
            <div class="mb-1.5 flex items-center justify-between">
              <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Descripción</label>
              <app-wf-variable-picker [target]="descriptionField()?.nativeElement ?? null" [disabled]="readOnly()"
                                      (valueChange)="patchConfig({ description: $event })" />
            </div>
            <textarea #descriptionInput rows="2" [class]="fieldClass" [value]="str('description')" [disabled]="readOnly()"
                      (input)="patchConfig({ description: inputValue($event) })"></textarea>
          </div>
          <div>
            <label [class]="labelClass">Inicio</label>
            <select [class]="fieldClass" [value]="nestedStr('start', 'type', 'RELATIVE')" [disabled]="readOnly()"
                    (change)="setStartType(inputValue($event))">
              <option value="RELATIVE">Relativo</option>
              <option value="FIXED">Fecha fija</option>
              <option value="PROPERTY_BASED">Basado en propiedad</option>
            </select>
          </div>
          @if (nestedStr('start', 'type', 'RELATIVE') === 'FIXED') {
            <div>
              <label [class]="labelClass">Fecha y hora</label>
              <input type="datetime-local" [class]="fieldClass" [value]="nestedStr('start', 'datetime')" [disabled]="readOnly()"
                     (change)="patchNested('start', { type: 'FIXED', datetime: inputValue($event) })" />
            </div>
          } @else {
            <div class="grid grid-cols-2 gap-3">
              @if (nestedStr('start', 'type', 'RELATIVE') === 'PROPERTY_BASED') {
                <div>
                  <label [class]="labelClass">Propiedad</label>
                  <app-wf-property-select
                    [value]="nestedStr('start', 'property')" [options]="propertyOptions()" [disabled]="readOnly()"
                    placeholder="Elige una propiedad…"
                    (valueChange)="patchNested('start', { type: 'PROPERTY_BASED', property: $event })"></app-wf-property-select>
                </div>
              } @else {
                <div>
                  <label [class]="labelClass">Cantidad</label>
                  <input type="number" min="1" [class]="fieldClass" [value]="nestedNum('start', 'amount', 2)" [disabled]="readOnly()"
                         (change)="patchNested('start', { type: 'RELATIVE', amount: numberValue($event) })" />
                </div>
              }
              <div>
                <label [class]="labelClass">Unidad</label>
                <select [class]="fieldClass" [value]="nestedStr('start', 'unit', 'DAYS')" [disabled]="readOnly()"
                        (change)="patchNested('start', { unit: inputValue($event) })">
                  @for (u of unitOptions; track u.value) { <option [value]="u.value">{{ u.label }}</option> }
                </select>
              </div>
              <div>
                <label [class]="labelClass">Hora</label>
                <input type="time" [class]="fieldClass" [value]="nestedStr('start', 'time', '10:00')" [disabled]="readOnly()"
                       (change)="patchNested('start', { time: inputValue($event) })" />
              </div>
              <div>
                <label [class]="labelClass">Zona horaria</label>
                <!-- Desplegable y NO texto libre: escribir mal el identificador IANA hacía que el
                     backend descartara la zona y agendara en otra hora sin avisar. -->
                <select [class]="fieldClass" [value]="startTimezone()" [disabled]="readOnly()"
                        (change)="patchNested('start', { timezone: inputValue($event) })">
                  @for (tz of timezoneOptions; track tz) { <option [value]="tz">{{ tz }}</option> }
                </select>
              </div>
            </div>
          }
          <div>
            <label [class]="labelClass">Duración (minutos)</label>
            <input type="number" min="1" [class]="fieldClass" [value]="num('durationMinutes', 30)" [disabled]="readOnly()"
                   (change)="patchConfig({ durationMinutes: numberValue($event) })" />
          </div>
          <div>
            <label [class]="labelClass">Tipo de reunión</label>
            <!-- Elegible: cada tipo puede tener sus propias franjas horarias, y con el tipo fijado
                 al primero del catálogo la acción fallaba con «no permite agendar en el día
                 seleccionado» sin que se viera de dónde salía la restricción. -->
            <select [class]="fieldClass" [value]="str('meetingTypeId')" [disabled]="readOnly()"
                    (change)="patchConfig({ meetingTypeId: numberValue($event) || null })">
              <option value="">Primer tipo activo del catálogo</option>
              @for (t of meetingTypeOptions(); track t.value) { <option [value]="t.value">{{ t.label }}</option> }
            </select>
            <p [class]="hintClass">
              Si el tipo tiene franjas horarias configuradas (Reuniones › Tipos), la fecha
              calculada debe caer dentro de una de ellas o el paso fallará.
            </p>
          </div>
          <!-- Modalidad: el módulo de Reuniones exige lugar (presencial) o proveedor (virtual).
               Sin estos campos la acción fallaba siempre al ejecutarse. -->
          <div>
            <label [class]="labelClass">Modalidad</label>
            <select [class]="fieldClass" [value]="meetingModality()" [disabled]="readOnly()"
                    (change)="setMeetingModality(inputValue($event))">
              <option value="VIRTUAL">Virtual (videollamada)</option>
              <option value="PRESENCIAL">Presencial (lugar)</option>
            </select>
          </div>
          @if (meetingModality() === 'VIRTUAL') {
            <div>
              <label [class]="labelClass">Proveedor de videollamada</label>
              <select [class]="fieldClass" [value]="str('videoProvider', 'jitsi')" [disabled]="readOnly()"
                      (change)="patchConfig({ videoProvider: inputValue($event), location: '' })">
                @for (p of videoProviderOptions; track p.value) { <option [value]="p.value">{{ p.label }}</option> }
              </select>
            </div>
          } @else {
            <div>
              <label [class]="labelClass">Lugar</label>
              <input type="text" [class]="fieldClass" [value]="str('location')" [disabled]="readOnly()"
                     placeholder="Ej. Oficina central, Av. Principal 123"
                     (input)="patchConfig({ location: inputValue($event), videoProvider: '' })" />
            </div>
          }
          <label class="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
            <input type="checkbox" class="rounded border-gray-300" [checked]="bool('ownerAsOrganizer', true)" [disabled]="readOnly()"
                   (change)="patchConfig({ ownerAsOrganizer: checkboxValue($event) })" />
            El responsable del registro es el organizador
          </label>
          @if (!bool('ownerAsOrganizer', true)) {
            <div>
              <label [class]="labelClass">Organizador</label>
              <app-entity-select
                [value]="num('organizerUserId') || null" [displayText]="organizerName()"
                [searchFn]="userSearch" [labelFn]="userLabel" [sublabelFn]="userSublabel" [disabled]="readOnly()"
                placeholder="Buscar usuario…"
                (valueChange)="patchConfig({ organizerUserId: $event })" (selected)="onOrganizerSelected($event)"></app-entity-select>
            </div>
          }
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Invitados</label>
              <button type="button" class="text-xs font-medium text-brand-500 hover:text-brand-600 disabled:opacity-40"
                      [disabled]="readOnly()" (click)="addAttendee()">+ Añadir</button>
            </div>
            @for (row of attendees(); track $index) {
              <div class="flex items-center gap-2">
                <select class="w-40 rounded-lg border border-gray-300 px-2 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                        [value]="row.type" [disabled]="readOnly()" (change)="updateAttendee($index, { type: inputValue($event) })">
                  <option value="CURRENT_RECORD_EMAIL">Correo del registro</option>
                  <option value="STATIC_EMAIL">Correo fijo</option>
                </select>
                @if (row.type === 'CURRENT_RECORD_EMAIL') {
                  <div class="flex-1 min-w-0">
                    <app-wf-property-select
                      [value]="row.emailProperty" [options]="propertyOptions()" [disabled]="readOnly()"
                      placeholder="propiedad de correo"
                      (valueChange)="updateAttendee($index, { emailProperty: $event })"></app-wf-property-select>
                  </div>
                } @else {
                  <input type="text" [class]="fieldClass" [value]="row.email" [disabled]="readOnly()" placeholder="correo@dominio.com"
                         (input)="updateAttendee($index, { email: inputValue($event) })" />
                }
                <button type="button" class="flex h-8 w-8 shrink-0 items-center justify-center rounded text-gray-400 hover:text-error-500 disabled:opacity-40"
                        [disabled]="readOnly()" (click)="removeAttendee($index)"><i class="fa-solid fa-xmark text-xs"></i></button>
              </div>
            }
          </div>
          <label class="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
            <input type="checkbox" class="rounded border-gray-300" [checked]="bool('createCalendarEvent', true)" [disabled]="readOnly()"
                   (change)="patchConfig({ createCalendarEvent: checkboxValue($event) })" />
            Crear evento en el calendario
          </label>
          <ng-container [ngTemplateOutlet]="associate" />
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }
    }

    <ng-template #associate>
      <label class="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
        <input type="checkbox" class="rounded border-gray-300" [checked]="bool('associateToCurrentRecord', true)" [disabled]="readOnly()"
               (change)="patchConfig({ associateToCurrentRecord: checkboxValue($event) })" />
        Asociar al registro actual
      </label>
    </ng-template>

    <ng-template #continueOnError>
      <label class="flex items-center gap-2 border-t border-gray-100 pt-3 text-xs text-gray-700 dark:border-gray-800 dark:text-gray-300">
        <input type="checkbox" class="rounded border-gray-300" [checked]="bool('continueOnError')" [disabled]="readOnly()"
               (change)="patchConfig({ continueOnError: checkboxValue($event) })" />
        Continuar el flujo si esta acción falla
      </label>
    </ng-template>
  `
})
export class WorkflowTaskActivityActionConfigComponent extends WorkflowNodeConfigBase {
  private readonly directory = inject(UserDirectoryApiService);
  private readonly taskCatalog = inject(TaskCatalogApiService);
  private readonly taskApi = inject(TaskApiService);
  private readonly meetingTypes = inject(MeetingTypeApiService);
  private readonly auth = inject(AuthService);

  protected readonly unitOptions = WAIT_UNIT_OPTIONS;
  protected readonly subtype = computed(() => this.node().nodeSubtype ?? '');

  /**
   * Catálogo real de tipos/prioridades de tarea del negocio. El backend
   * (WorkflowTaskActivityActionService.resolveTaskTypeId/resolveTaskPriorityId) resuelve
   * `config.type`/`config.priority` comparando (case-insensitive) contra el NOMBRE o la
   * ETIQUETA del catálogo → el value guardado es el `name` del catálogo (misma clave que el
   * backend empareja). Si el negocio no tiene catálogo cargado, se cae a los enums fijos previos.
   */
  private readonly catalogTaskTypes = signal<{ value: string; label: string }[]>([]);
  private readonly catalogPriorities = signal<{ value: string; label: string }[]>([]);
  // INC_CRM_020 (8) inc38 — estas listas ya traen etiqueta en español; el `value` sigue siendo la
  // clave que el backend empareja contra el catálogo, por eso NO se mapea label = value.
  private readonly fixedTaskTypes = TASK_TYPE_OPTIONS;
  private readonly fixedPriorities = PRIORITY_OPTIONS;
  protected readonly taskTypeOptions = computed(() => this.catalogTaskTypes().length ? this.catalogTaskTypes() : this.fixedTaskTypes);
  protected readonly priorityOptions = computed(() => this.catalogPriorities().length ? this.catalogPriorities() : this.fixedPriorities);
  /** Conserva como opción el valor ya guardado que no esté en el catálogo (no perder datos al editar). */
  /** Mismos valores que el formulario de Reuniones (`meeting-form`), que es el contrato del backend. */
  protected readonly videoProviderOptions = [
    { value: 'jitsi', label: 'Jitsi' },
    { value: 'google', label: 'Google Meet' },
    { value: 'microsoft', label: 'Microsoft Teams' },
    { value: 'zoom', label: 'Zoom' },
    { value: 'webex', label: 'Webex' },
    { value: 'other', label: 'Otro' }
  ];

  protected readonly timezoneOptions = WORKFLOW_TIMEZONE_OPTIONS;

  /** Tipos de reunión activos del negocio, para que el paso no dependa del primero del catálogo. */
  protected readonly meetingTypeOptions = signal<{ value: number; label: string }[]>([]);

  /**
   * Zona con la que se interpretará la hora del nodo.
   *
   * Refleja el MISMO valor por defecto que aplica el backend
   * (`WorkflowTaskActivityActionService.DEFAULT_TIMEZONE`) cuando la config no trae zona. Antes
   * el campo mostraba `America/Lima` mientras el backend calculaba en UTC, así que una reunión
   * «a las 10:00» se creaba a las 05:00 y nada en pantalla lo delataba.
   */
  protected readonly startTimezone = computed(
    () => this.nestedStr('start', 'timezone', '') || DEFAULT_WORKFLOW_TIMEZONE
  );

  /** La modalidad no se guarda: se deduce de cuál de los dos campos tiene valor. */
  protected meetingModality(): 'VIRTUAL' | 'PRESENCIAL' {
    return this.str('location') ? 'PRESENCIAL' : 'VIRTUAL';
  }

  protected setMeetingModality(value: string): void {
    if (value === 'PRESENCIAL') {
      this.patchConfig({ videoProvider: '', location: this.str('location') || '' });
    } else {
      this.patchConfig({ location: '', videoProvider: this.str('videoProvider') || 'jitsi' });
    }
  }

  // Sin valor por defecto: `CALL`/`HIGH` no existen en el catálogo de Tareas de un negocio real
  // (que está en español) y la acción moría en ejecución. Vacío = "el primero del catálogo".
  protected readonly customTypeValue = computed(() => {
    const v = this.str('type');
    return v && !this.taskTypeOptions().some((o) => o.value === v) ? v : '';
  });
  protected readonly customPriorityValue = computed(() => {
    const v = this.str('priority');
    return v && !this.priorityOptions().some((o) => o.value === v) ? v : '';
  });
  protected readonly customTaskSelectorType = computed(() => {
    const v = this.nestedStr('taskSelector', 'taskType');
    return v && !this.taskTypeOptions().some((o) => o.value === v) ? v : '';
  });

  /** COMPLETE_TASK · EXPLICIT_TASK_ID: nombre de la tarea elegida (para el buscador por título). */
  protected readonly taskName = signal('');
  protected readonly taskSelectorId = computed(() => this.nestedNum('taskSelector', 'taskId', 0));

  /** Propiedades sugeridas del objeto base del workflow (etiqueta legible + clave). */
  protected readonly propertyOptions = computed(() => buildPropertyOptions(this.editor.workflow()?.objectType ?? null));

  /**
   * Nombres del responsable / organizador guardados, resueltos por la caché del directorio
   * (modo `ids`). Un usuario dado de baja se marca en vez de esconderse.
   */
  protected readonly assigneeName = computed(() => this.userDisplayName(this.assigneeUserId()));
  protected readonly organizerName = computed(() => this.userDisplayName(this.num('organizerUserId')));

  /** Nodos a los que ya se les sembraron los valores por defecto (ver persistVisibleDefaults). */
  private readonly seededNodes = new Set<string>();

  // Campos destino del variable-picker (un viewChild por concepto; el @switch deja uno visible).
  protected readonly titleField = viewChild<ElementRef<HTMLInputElement>>('titleInput');
  protected readonly descriptionField = viewChild<ElementRef<HTMLTextAreaElement>>('descriptionInput');
  protected readonly notesField = viewChild<ElementRef<HTMLTextAreaElement>>('notesInput');
  protected readonly noteTextField = viewChild<ElementRef<HTMLTextAreaElement>>('noteTextInput');
  protected readonly completionNoteField = viewChild<ElementRef<HTMLInputElement>>('completionNoteInput');

  constructor() {
    super();
    effect(() => this.persistVisibleDefaults());
    effect(() => this.directory.resolveUsers([this.assigneeUserId(), this.num('organizerUserId')]));
    effect(() => this.resolveTaskName(this.taskSelectorId()));
    this.taskCatalog.getActiveTypes().subscribe({
      next: (list) => this.catalogTaskTypes.set(
        (list ?? []).filter((t) => t.name).map((t) => ({ value: t.name, label: t.label || t.name }))
      ),
      error: () => {}
    });
    this.taskCatalog.getActivePriorities().subscribe({
      next: (list) => this.catalogPriorities.set(
        (list ?? []).filter((p) => p.name).map((p) => ({ value: p.name, label: p.label || p.name }))
      ),
      error: () => {}
    });
    this.meetingTypes.listActive(this.auth.getCurrentUser()?.businessId || 1).subscribe({
      next: (list) => this.meetingTypeOptions.set(
        (list ?? []).filter((t) => t.id != null).map((t) => ({ value: t.id, label: t.label || t.name || `Tipo #${t.id}` }))
      ),
      error: () => {}
    });
  }

  /**
   * Escribe en la config los valores por defecto que el panel YA está mostrando.
   *
   * `Vencimiento` (Relativo · 1 · DAYS) y `Asignar a` (Responsable del registro) se pintaban con
   * los fallbacks de `nestedStr(...)`, que son solo de vista: mientras el usuario no tocara esos
   * desplegables, `due` y `assignee` no existían en la config y el checklist de activación
   * rechazaba el nodo con «CREATE_TASK requiere…» aunque en pantalla se viera todo relleno.
   * Sembrarlos al abrir el inspector hace que lo que se ve sea lo que se guarda.
   */
  private persistVisibleDefaults(): void {
    const node = this.node();
    const seedable = node.nodeSubtype === 'CREATE_TASK' || node.nodeSubtype === 'SCHEDULE_MEETING';
    if (!seedable || this.readOnly() || this.seededNodes.has(node.nodeKey)) {
      return;
    }
    const current = (node.config ?? {}) as Record<string, unknown>;
    const patch: Record<string, unknown> = {};
    if (node.nodeSubtype === 'SCHEDULE_MEETING') {
      // La zona se pintaba con el fallback de `nestedStr` y no llegaba a la config, así que el
      // backend la calculaba en UTC: «10:00» acababa siendo las 05:00. Sembrarla la hace real.
      const start = (current['start'] ?? {}) as Record<string, unknown>;
      if (!start['timezone']) {
        patch['start'] = { ...start, timezone: DEFAULT_WORKFLOW_TIMEZONE };
      }
    } else {
      if (!current['due']) {
        patch['due'] = { type: 'RELATIVE', amount: 1, unit: 'DAYS' };
      }
      if (!current['assignee']) {
        patch['assignee'] = { type: 'OWNER' };
      }
    }
    // Marcar ANTES de escribir: `patchConfig` muta el nodo del store y vuelve a disparar este
    // effect; sin el guard se reentraría en bucle y el lienzo se quedaba colgado.
    this.seededNodes.add(node.nodeKey);
    if (Object.keys(patch).length > 0) {
      untracked(() => this.patchConfig(patch));
    }
  }

  private resolveTaskName(id: number): void {
    if (!id) {
      this.taskName.set('');
      return;
    }
    this.taskApi.getTaskById(id).subscribe({
      next: (t) => this.taskName.set(this.taskLabel(t)),
      error: () => {}
    });
  }

  // ── COMPLETE_TASK · búsqueda de tarea por título ─────────────────────────────
  taskSearch = (term: string, page: number): Observable<EntitySelectPage<TaskResponse>> => {
    return this.taskApi.filterTasks({ searchTerm: term || undefined, page, size: 10 }).pipe(
      map((r) => ({ items: r.content, totalPages: r.totalPages })),
      catchError(() => of({ items: [] as TaskResponse[], totalPages: 0 }))
    );
  };
  taskLabel = (t: TaskResponse): string => t.title || `Tarea #${t.id}`;
  taskSublabel = (t: TaskResponse): string => (t.id != null ? `#${t.id}` : '');
  protected onTaskSelected(t: TaskResponse | null): void {
    this.taskName.set(t ? this.taskLabel(t) : '');
  }

  protected assigneeUserId(): number {
    return this.nestedNum('assignee', 'userId', 0);
  }

  /** Nombre a mostrar de un id de usuario guardado (vacío mientras el directorio responde). */
  private userDisplayName(id: number): string {
    if (!id) {
      return '';
    }
    const user = this.directory.knownUsers().get(id);
    if (!user) {
      return this.directory.unresolvedUserIds().has(id) ? `Usuario #${id} (no disponible)` : '';
    }
    return `${directoryUserLabel(user)}${user.isActive === false ? ' (inactivo)' : ''}`;
  }

  // ── due / start / selector helpers ───────────────────────────────────────────
  protected setDueType(type: string): void {
    this.patchNested('due', { type });
  }
  protected setStartType(type: string): void {
    this.patchNested('start', { type });
  }
  protected setSelectorType(type: string): void {
    this.patchNested('taskSelector', { type });
  }
  protected setAssigneeType(type: string): void {
    this.patchNested('assignee', { type });
  }

  protected onAssigneeSelected(user: DirectoryUser | null): void {
    this.directory.remember(user);
  }
  protected onOrganizerSelected(user: DirectoryUser | null): void {
    this.directory.remember(user);
  }

  // ── attendees ────────────────────────────────────────────────────────────────
  protected readonly attendees = computed<AttendeeRow[]>(() => {
    const raw = this.arr('attendees');
    if (!raw.length) {
      return [{ type: 'CURRENT_RECORD_EMAIL', emailProperty: 'email', email: '' }];
    }
    return raw.map((item) => {
      const r = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
      return {
        type: String(r['type'] ?? 'CURRENT_RECORD_EMAIL'),
        emailProperty: String(r['emailProperty'] ?? 'email'),
        email: String(r['email'] ?? '')
      };
    });
  });

  protected addAttendee(): void {
    this.patchConfig({ attendees: [...this.attendees(), { type: 'CURRENT_RECORD_EMAIL', emailProperty: 'email', email: '' }] });
  }
  protected updateAttendee(index: number, patch: Partial<AttendeeRow>): void {
    this.patchConfig({ attendees: this.attendees().map((r, i) => (i === index ? { ...r, ...patch } : r)) });
  }
  protected removeAttendee(index: number): void {
    this.patchConfig({ attendees: this.attendees().filter((_, i) => i !== index) });
  }

  // ── user select (directorio unificado, búsqueda en servidor) ─────────────────
  userSearch = (term: string): Observable<EntitySelectPage<DirectoryUser>> =>
    this.directory.searchUsers(term, 20).pipe(
      map((list) => ({ items: list, totalPages: 1 })),
      catchError(() => of({ items: [] as DirectoryUser[], totalPages: 0 }))
    );
  userLabel = (user: DirectoryUser): string => directoryUserLabel(user);
  userSublabel = (user: DirectoryUser): string => user.mail ?? '';
}
